import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useSession } from "../Login/SessionContext";
import { API_HOST } from "../../constants";
import { getCookie } from "../../utils/cookieUtil";
import "react-quill/dist/quill.snow.css";
import ReactQuill from "react-quill";
import LoadingAnime from "../Common/LoadingAnime";
import Button from "../ui/Button";
import TagInput from "./TagInput";

function BbsWrite() {
  const { isLoading, user } = useSession();
  const { moduleName } = useParams();
  const navigate = useNavigate();

  const quillRef = useRef(null);

  const [module, setModule] = useState({});
  const [categories, setCategories] = useState([]);
  const [newPost, setNewPost] = useState({
    title: "",
    category: "",
    content: "",
  });
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModule = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_HOST}/api/modules/`, {
          params: { module_name: moduleName },
        });

        const moduleData = response.data[0];
        if (moduleData) {
          setModule(moduleData);

          try {
            const categoriesResponse = await axios.get(
              `${API_HOST}/api/bbs/${moduleName}/categories/`
            );
            setCategories(categoriesResponse.data);
          } catch {
            setError("카테고리를 가져오는 중 오류가 발생했습니다.");
          }
        } else {
          setError("게시판 모듈을 찾을 수 없습니다.");
        }
      } catch {
        setError("게시판 모듈을 가져오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchModule();
  }, [moduleName]);

  // ✅ 이미지 업로드
  const handleImageUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const csrfToken = getCookie("csrftoken");

      const response = await axios.post(`${API_HOST}/api/upload/`, formData, {
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      return `${API_HOST}${response.data.imageUrl}`;
    } catch {
      setError("이미지를 업로드하는 중 오류가 발생했습니다.");
      return null;
    }
  }, []);

  // ✅ 이미지 핸들러 (리렌더 방지)
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.click();

    input.onchange = async () => {
      const files = input.files;
      if (!files?.length) return;

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const range = editor.getSelection(true);

      for (const file of files) {
        const imageUrl = await handleImageUpload(file);
        if (imageUrl) {
          editor.insertEmbed(range.index, "image", imageUrl);
          editor.insertText(range.index + 1, "\n");
        }
      }
    };
  }, [handleImageUpload]);

  // ✅ modules 고정 (⭐⭐⭐⭐⭐ 핵심)
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [imageHandler]
  );

  const formats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "link",
      "image",
    ],
    []
  );

  const handleAddPost = async () => {
    const { title, category, content } = newPost;

    if (!title || !content) {
      alert("제목과 내용을 입력해주세요.");
      return;
    }

    try {
      const csrfToken = getCookie("csrftoken");

      const response = await axios.post(
        `${API_HOST}/api/bbs/`,
        {
          title,
          category,
          author: user.id,
          board_module: module.id,
          content,
          tags,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        }
      );

      setNewPost({ title: "", category: "", content: "" });
      setTags([]);

      const createdId = response?.data?.document_id;
      navigate(createdId ? `/bbs/${moduleName}/${createdId}` : `/bbs/${moduleName}/`);
    } catch {
      setError("게시물을 추가하는 중 오류가 발생했습니다.");
    }
  };

  if (isLoading || loading) {
    return <LoadingAnime label="게시글 작성 준비 중..." />;
  }

  return (
    <div className="space-y-6">
      <section className="glass-card p-6">
        <h2 className="text-2xl font-semibold text-white">
          {module.name || "게시물 작성"}
        </h2>
        <p className="mt-1 text-sm text-ink-200">
          새로운 이야기를 공유해보세요.
        </p>
      </section>

      {error && (
        <section className="glass-card p-4 text-sm text-red-200">
          {error}
        </section>
      )}

      <section className="glass-card p-6 space-y-4">
        {/* 카테고리 */}
        <div>
          <label className="text-xs font-semibold text-ink-200">카테고리</label>
          <select
            className="soft-input mt-2"
            value={newPost.category}
            onChange={(e) =>
              setNewPost((prev) => ({ ...prev, category: e.target.value }))
            }
          >
            <option value="">카테고리를 선택하세요</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* 제목 */}
        <div>
          <label className="text-xs font-semibold text-ink-200">제목</label>
          <input
            type="text"
            className="soft-input mt-2"
            placeholder="제목을 입력하세요"
            value={newPost.title}
            onChange={(e) =>
              setNewPost((prev) => ({ ...prev, title: e.target.value }))
            }
          />
        </div>

        {/* 내용 */}
        <div>
          <label className="text-xs font-semibold text-ink-200">내용</label>
          <div className="mt-2">
            <ReactQuill
              ref={quillRef}
              value={newPost.content}
              onChange={(content) =>
                setNewPost((prev) => ({ ...prev, content }))
              }
              modules={modules}
              formats={formats}
              theme="snow"
            />
          </div>
        </div>

        {/* 태그 */}
        <div>
          <label className="text-xs font-semibold text-ink-200">태그</label>
          <div className="mt-2">
            <TagInput value={tags} onChange={setTags} />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleAddPost}>작성</Button>
        </div>
      </section>
    </div>
  );
}

export default BbsWrite;